import {
    Component,
    Input,
    Output,
    EventEmitter,
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    ViewChild,
    ElementRef,
    signal,
    effect,
    input,
    computed,
} from "@angular/core";
import { TypeManagerDecorator } from "../../main/type.map.service";
import { FormsModule } from "@angular/forms";
import { createChatbotService, Message, S25ChatbotService, ServiceConfigMap } from "./s25.chatbot.service";
import { CommonModule } from "@angular/common";
import { S25IconButtonComponent } from "../../standalone/s25.icon.button.component";
import { S25ModalComponent } from "../s25-modal/s25.modal.component";
import { S25ModalModule } from "../s25-modal/s25.modal.module";
import { AiStore } from "./s25.ai.store";
import { S25SidebarComponent } from "../s25-sidebar/s25.sidebar.component";
import { SidebarStore } from "../s25-sidebar/s25.sidebar.store";
import { S25LoadingInlineModule } from "../s25-loading/s25.loading.inline.module";
import { S25IconComponent } from "../s25-icon/s25.icon.component";
import { AiFeature, AiTelemetry } from "../../services/ai.telemetry";
import type { TelemetrySession } from "../../services/telemetry/telemetry.session";
import { S25ButtonComponent } from "../../standalone/s25.button.component";

export type ChatbotMode = "modal" | "sidebar" | "inline" | "fixed";
export type ServiceConfig = "advancedSearch" | "expressScheduling" | "pricingFormula";

const SERVICE_CONFIG_TO_FEATURE: Record<ServiceConfig, AiFeature> = {
    advancedSearch: "Search",
    expressScheduling: "ExpressScheduling",
    pricingFormula: "PricingFormula",
};

@TypeManagerDecorator("s25-ng-ai-chatbot")
@Component({
    selector: "s25-ng-ai-chatbot",
    template: `
        @if (isInit && aiStore.hasAiEnabled() && !aiStore.isLoading()) {
            <ng-container>
                <ng-template #chatbotComponent>
                    <div class="chat-container">
                        <div class="chat-window" #chatWindow [style.--window-max-height]="windowMaxHeight">
                            @for (message of messages; track $index) {
                                <div class="{{ message.role }}-message">{{ message.content }}</div>
                            }
                            @if (isWaiting) {
                                <s25-ng-loading-inline-static [text]="null"></s25-ng-loading-inline-static>
                            }
                        </div>
                        <div class="chat-input">
                            <input
                                class="c-input"
                                type="text"
                                [value]="inputMessage()"
                                placeholder="Type your message..."
                                (input)="onInputChange($event.target.value, $event)"
                                (keydown.enter)="onSend()"
                                autocomplete="off"
                            />
                            <button
                                class="aw-button aw-button--primary"
                                (click)="onSend()"
                                [disabled]="this.isWaiting"
                                id="send-message-button"
                            >
                                Send
                            </button>
                        </div>
                    </div>
                </ng-template>

                @switch (mode) {
                    @case ("modal") {
                        <s25-ng-icon-button
                            (clicked)="openModal()"
                            [type]="'aiSparkles'"
                            [ariaLabel]="'Open ' + title"
                        ></s25-ng-icon-button>
                        <s25-ng-modal #chatBotModal [title]="title" [size]="'xxs'">
                            <ng-container *ngTemplateOutlet="chatbotComponent"></ng-container>
                        </s25-ng-modal>
                    }
                    @case ("sidebar") {
                        <s25-ng-sidebar [title]="title" [mode]="'push'" (closed)="onClosed()" [width]="300">
                            <ng-container *ngTemplateOutlet="chatbotComponent"></ng-container>
                        </s25-ng-sidebar>
                    }
                    @case ("fixed") {
                        @if (sidebarStore.tabVisible()) {
                            <div class="chatbot-fixed" [class.open]="sidebarStore.isOpen()">
                                <s25-ng-button
                                    class="toggle-tab"
                                    [type]="'none'"
                                    (clicked)="sidebarStore.toggle()"
                                    [class.open]="sidebarStore.isOpen()"
                                    [ariaLabel]="'Open AI Chat'"
                                    [ariaExpanded]="sidebarStore.isOpen()"
                                    [ariaControls]="sidebarStore.elementId"
                                >
                                    <div class="toggle-tab--button">
                                        <s25-ng-icon
                                            class="toggle-tab-icon"
                                            [class.open]="sidebarStore.isOpen()"
                                            [type]="'doubleArrowLeft'"
                                            [ariaLabel]="false"
                                        />
                                        <s25-ng-icon [type]="'aiSparkles'" [ariaLabel]="false" />
                                    </div>
                                </s25-ng-button>
                                @if (sidebarStore.isOpen()) {
                                    <div class="chat-body">
                                        <div class="chat-header">
                                            <h2>{{ title }}</h2>
                                            <s25-ng-icon-button
                                                class="s25-modal--close"
                                                [type]="'close'"
                                                [ariaLabel]="'Close chat'"
                                                (clicked)="onClosed()"
                                            />
                                        </div>
                                        <ng-container *ngTemplateOutlet="chatbotComponent"></ng-container>
                                    </div>
                                }
                            </div>
                        }
                    }
                    @default {
                        <ng-container *ngTemplateOutlet="chatbotComponent"></ng-container>
                    }
                }
            </ng-container>
        }
    `,
    styles: [
        `
            .chatbot-fixed {
                position: fixed;
                display: flex;
                top: 2.5rem;
                right: 0;
                max-width: 45%;
                height: var(--window-max-height, 360px);

                z-index: 1100;
            }

            .chatbot-fixed.open {
                right: 1.5rem;
            }

            .chatbot-fixed .chat-body {
                display: flex;
                flex-direction: column;
                height: 100%;
                background-color: white;
                box-shadow: -2px 0 8px rgba(0, 0, 0, 0.15);
            }

            .chatbot-fixed .chat-container {
                display: flex;
                flex-direction: column;
                flex: 1;
                overflow-y: auto;
            }

            .chat-header {
                display: flex;
                justify-content: space-between;
                padding: 1rem;
            }

            .toggle-tab {
                display: flex;
                flex-direction: column;
                flex-wrap: wrap;
                max-height: fit-content;
                background: var(--cn-blue);
                border: 1px solid rgba(0, 0, 0, 0.08);
                border-radius: 4px 0 0 4px;
                padding: 0.5rem;
                cursor: pointer;
                color: #fff;

                transition: margin-right 0.5s ease;
            }

            .toggle-tab.open {
                transition: transform 0.5s ease;
            }

            .toggle-tab-icon.open {
                --icon--rotation-degrees: 180deg;
            }

            /* Container for chat */
            .chat-container {
                text-align: start;
            }

            /* Chat window for messages */
            .chat-window {
                display: flex;
                flex-direction: column;
                flex-grow: 1;
                overflow-y: scroll;
                padding: 2%;
                margin-bottom: 2.5px;
                border-bottom: 1px solid #e0e0e0;
                max-height: var(--window-max-height, 360px);
            }

            /* Any messages */
            .user-message,
            .assistant-message {
                padding: 10px;
                border-radius: 15px;
                margin-bottom: 5px;
                max-width: 80%;
                word-wrap: break-word;
                white-space: pre-wrap;
            }

            /* User message */
            .user-message {
                align-self: flex-end;
                background-color: #007bff;
                color: white;
                border-radius: 15px 15px 1px 15px;
            }

            /* AI response */
            .assistant-message {
                align-self: flex-start;
                background-color: #e1e1e1;
                color: #333;
                border-radius: 15px 15px 15px 1px;
            }

            /* Input section */
            .chat-input {
                display: flex;
                align-items: center;
                padding: 2%;
            }

            /* In order to avoid send button leaving column bounds */
            @media only screen and (max-width: 1060px) {
                .chat-input {
                    flex-wrap: wrap;
                }

                .chat-input input {
                    margin-bottom: 10px;
                }
            }

            /* Text input */
            .chat-input input {
                flex-grow: 1;
                margin-right: 10px;
                font-size: 16px;
            }
        `,
    ],
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [
        FormsModule,
        CommonModule,
        S25ModalModule,
        S25SidebarComponent,
        S25IconComponent,
        S25IconButtonComponent,
        S25LoadingInlineModule,
        S25ButtonComponent,
    ],
    standalone: true,
})
export class S25ChatbotComponent<ConfigKey extends ServiceConfig> {
    @Input() welcomeMessage: string;

    // This will cause the component to call PreferenceService to get the custom chatbot name before init.
    @Input() useCustomName: boolean = true;

    // This will determine the element the chatbot is contained in.
    @Input() mode: ChatbotMode = "inline";

    // Title is used for sidebar and modal headers
    @Input() title: string;

    // Determines the expected response shape, as well as the URL (configs can be found in s25.chatbot.service.ts)
    readonly serviceConfig = input.required<ConfigKey>();

    @Input() windowMaxHeight: string = "360px";

    readonly aiSession = input<TelemetrySession | undefined>(undefined);

    readonly session = computed(
        () => this.aiSession() ?? AiTelemetry.createSession(SERVICE_CONFIG_TO_FEATURE[this.serviceConfig()]),
    );

    // @Output() messageThreadSent = new EventEmitter<Message[]>();
    @Output() messageSent = new EventEmitter();
    @Output() responseReceived = new EventEmitter<ServiceConfigMap[ConfigKey]>();

    @ViewChild("chatWindow") chatWindow!: ElementRef;
    @ViewChild("chatBotModal") chatBotModal!: S25ModalComponent;

    isInit: boolean = false;
    isWaiting: boolean = false;
    messages: Message[] = [];
    inputMessage = signal<string>("");

    private chatbotService!: S25ChatbotService<ServiceConfigMap[ConfigKey]>;

    constructor(
        private cd: ChangeDetectorRef,
        public sidebarStore: SidebarStore,
        public aiStore: AiStore,
    ) {
        effect(() => {
            if (aiStore.hasAiEnabled() && aiStore.customName() && !this.isInit) {
                this.init();
            }
        });
    }

    init() {
        this.isInit = true;
        this.chatbotService = createChatbotService(this.serviceConfig());
        this.populateOpeningMessage();
        this.cd.detectChanges();
    }

    populateOpeningMessage() {
        if (!this.welcomeMessage) return;
        this.useCustomName && this.aiStore.customName()
            ? this.addResponse(`Hi, I'm ${this.aiStore.customName()}, an AI-powered assistant. ` + this.welcomeMessage)
            : this.addResponse(this.welcomeMessage);
    }

    openModal() {
        return this.chatBotModal.open();
    }

    closeModal() {
        return this.chatBotModal.close();
    }

    onClosed() {
        if (this.hasUserMessages()) {
            this.session().close();
        }
        if (this.mode == "fixed" || this.mode == "sidebar") {
            this.sidebarStore.close();
        }
        if (this.messages.length > 1) {
            this.resetChat();
        }
    }

    onInputChange(value: string, event: Event) {
        this.inputMessage.set(value);
        if (value.trim()) {
            this.session().engage();
        }
    }

    onSend() {
        if (this.isWaiting) return;
        this.isWaiting = true;
        this.sendMessageThread();
    }

    async sendMessageThread() {
        if (!this.inputMessage().trim()) {
            return this.cd.detectChanges();
        }

        this.messages.push({ role: "user", content: this.inputMessage().trim() });
        this.inputMessage.set("");
        this.cd.detectChanges();
        this.scrollToBottom();
        try {
            const resp = await this.chatbotService.sendMessageThread(this.messages);
            if (!resp) this.throwError();
            this.responseReceived.emit(resp);
        } catch {
            this.throwError();
        }
    }

    addResponse(response: string) {
        this.messages.push({
            role: "assistant",
            content: response || "An error has occurred. Please refresh and try again.",
        });
        this.isWaiting = false;
        this.cd.detectChanges();
        this.scrollToBottom();
    }

    throwError() {
        this.addResponse("An error has occurred. Please try again.");
        this.isWaiting = false;
    }

    resetChat() {
        this.messages = [];
        this.populateOpeningMessage();
        this.isWaiting = false;
    }

    scrollToBottom() {
        try {
            this.chatWindow.nativeElement.scrollTop = this.chatWindow.nativeElement.scrollHeight;
        } catch (err) {}
    }

    private hasUserMessages(): boolean {
        return this.messages.some((message) => message.role === "user");
    }
}
